import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Grid
} from '@mui/material';

import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Download
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

interface ReportSummary {
  totalBets: number;
  totalWinnings: number;
  totalLosses: number;
  netProfitLoss: number;
  winRate: number;
  totalTransactions: number;
}

interface BetReport {
  id: number;
  date: string;
  counter: string;
  number: string;
  betType: string;
  amount: number;
  result: 'win' | 'lose' | 'pending';
  winAmount?: number;
  drawDate: string;
}

interface DateRangeFilter {
  startDate: Date | null;
  endDate: Date | null;
}

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<'summary' | 'detailed'>('summary');
  const [dateRange, setDateRange] = useState<DateRangeFilter>({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date())
  });
  const [counter, setCounter] = useState<string>('all');
  const [betType, setBetType] = useState<string>('all');

  const counters = [
    'Magnum',
    'Damacai 4D',
    'Singapore 4D',
    'Sports Toto',
    'Cash Sweep',
    'Sabah 88',
    'STC 4D',
    'Grand Dragon Lotto',
    '9 Lotto'
  ];

  const betTypes = ['Big', 'Small', '4A', 'A', 'ABC', '5D', '6D', '2A'];

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch report summary
  const { data: reportSummary, isLoading: summaryLoading } = useQuery<ReportSummary>({
    queryKey: ['report-summary', dateRange, counter, betType],
    queryFn: async () => {
      const params = {
        startDate: dateRange.startDate ? formatLocalDate(dateRange.startDate) : undefined,
        endDate: dateRange.endDate ? formatLocalDate(dateRange.endDate) : undefined,
        counter: counter !== 'all' ? counter : undefined,
        betType: betType !== 'all' ? betType : undefined
      };
      const response = await axios.get('/admin/reports/summary', { params });
      return response.data;
    },
    // Mock data for development
    initialData: {
      totalBets: 2450.00,
      totalWinnings: 1890.00,
      totalLosses: 560.00,
      netProfitLoss: 1330.00,
      winRate: 77.2,
      totalTransactions: 45
    }
  });

  // Fetch detailed report
  const { data: detailedReport, isLoading: detailedLoading } = useQuery<BetReport[]>({
    queryKey: ['report-detailed', dateRange, counter, betType],
    queryFn: async () => {
      const params = {
        startDate: dateRange.startDate ? formatLocalDate(dateRange.startDate) : undefined,
        endDate: dateRange.endDate ? formatLocalDate(dateRange.endDate) : undefined,
        counter: counter !== 'all' ? counter : undefined,
        betType: betType !== 'all' ? betType : undefined
      };
      const response = await axios.get('/admin/reports/detailed', { params });
      return response.data;
    },
    enabled: reportType === 'detailed',
    // Mock data for development
    initialData: [
      {
        id: 1,
        date: '2024-01-15T10:30:00Z',
        counter: 'Magnum',
        number: '1234',
        betType: 'Big',
        amount: 50.00,
        result: 'win',
        winAmount: 150.00,
        drawDate: '2024-01-15T19:00:00Z'
      },
      {
        id: 2,
        date: '2024-01-14T14:20:00Z',
        counter: 'Sports Toto',
        number: '5678',
        betType: 'Small',
        amount: 25.00,
        result: 'lose',
        drawDate: '2024-01-14T19:00:00Z'
      },
      {
        id: 3,
        date: '2024-01-13T16:45:00Z',
        counter: 'Singapore 4D',
        number: '9012',
        betType: 'Big',
        amount: 100.00,
        result: 'win',
        winAmount: 300.00,
        drawDate: '2024-01-13T18:30:00Z'
      },
      {
        id: 4,
        date: '2024-01-12T11:15:00Z',
        counter: 'Damacai 4D',
        number: '3456',
        betType: 'Small',
        amount: 30.00,
        result: 'pending',
        drawDate: '2024-01-20T19:00:00Z'
      }
    ]
  });

  const handleQuickDateRange = (days: number) => {
    const endDate = new Date();
    const startDate = subDays(endDate, days);
    setDateRange({ startDate, endDate });
  };

  const handleExportReport = () => {
    // Implementation for exporting report as PDF/Excel
    console.log('Exporting report...');
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case 'win': return 'success';
      case 'lose': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0.00';
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Reports & Analytics
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Report Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={reportType}
                  label="Report Type"
                  onChange={(e) => setReportType(e.target.value as 'summary' | 'detailed')}
                >
                  <MenuItem value="summary">Summary</MenuItem>
                  <MenuItem value="detailed">Detailed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Counter</InputLabel>
                <Select
                  value={counter}
                  label="Counter"
                  onChange={(e) => setCounter(e.target.value)}
                >
                  <MenuItem value="all">All Counters</MenuItem>
                  {counters.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Bet Type</InputLabel>
                <Select
                  value={betType}
                  label="Bet Type"
                  onChange={(e) => setBetType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {betTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportReport}
                fullWidth
              >
                Export Report
              </Button>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => handleQuickDateRange(7)}>Last 7 Days</Button>
                <Button size="small" onClick={() => handleQuickDateRange(30)}>Last 30 Days</Button>
                <Button size="small" onClick={() => handleQuickDateRange(90)}>Last 3 Months</Button>
                <Button 
                  size="small" 
                  onClick={() => setDateRange({
                    startDate: startOfMonth(new Date()),
                    endDate: endOfMonth(new Date())
                  })}
                >
                  This Month
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Report */}
        {reportType === 'summary' && (
          <>
            {summaryLoading && <LinearProgress sx={{ mb: 2 }} />}
            
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Bets
                        </Typography>
                        <Typography variant="h5">
                          {formatCurrency(reportSummary?.totalBets || 0)}
                        </Typography>
                      </Box>
                      <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Winnings
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {formatCurrency(reportSummary?.totalWinnings || 0)}
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Losses
                        </Typography>
                        <Typography variant="h5" color="error.main">
                          {formatCurrency(reportSummary?.totalLosses || 0)}
                        </Typography>
                      </Box>
                      <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Net P&L
                        </Typography>
                        <Typography 
                          variant="h5" 
                          color={(reportSummary?.netProfitLoss || 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(reportSummary?.netProfitLoss || 0)}
                        </Typography>
                      </Box>
                      {(reportSummary?.netProfitLoss || 0) >= 0 ? 
                        <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} /> :
                        <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                      }
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Win Rate
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={reportSummary?.winRate || 0}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                      </Box>
                      <Typography variant="h6">
                        {(reportSummary?.winRate || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Based on {reportSummary?.totalTransactions || 0} transactions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Total Transactions:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {reportSummary?.totalTransactions || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Average Bet:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(
                            (reportSummary?.totalBets || 0) / (reportSummary?.totalTransactions || 1)
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">ROI:</Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          color={(reportSummary?.netProfitLoss || 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {(((reportSummary?.netProfitLoss || 0) / (reportSummary?.totalBets || 1)) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* Detailed Report */}
        {reportType === 'detailed' && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Transaction Report
            </Typography>
            
            {detailedLoading && <LinearProgress sx={{ mb: 2 }} />}
            
            {detailedReport && detailedReport.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Counter</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Bet Amount</TableCell>
                      <TableCell align="center">Result</TableCell>
                      <TableCell align="right">Win Amount</TableCell>
                      <TableCell align="right">Net P&L</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailedReport.map((bet) => (
                      <TableRow key={bet.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(bet.date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Draw: {formatDate(bet.drawDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>{bet.counter}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {bet.number}
                          </Typography>
                        </TableCell>
                        <TableCell>{bet.betType}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(bet.amount)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={bet.result}
                            color={getResultColor(bet.result) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {bet.winAmount ? formatCurrency(bet.winAmount) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              bet.result === 'win' ? 'success.main' :
                              bet.result === 'lose' ? 'error.main' : 'text.primary'
                            }
                          >
                            {bet.result === 'win' && bet.winAmount
                              ? `+${formatCurrency(bet.winAmount - bet.amount)}`
                              : bet.result === 'lose'
                              ? `-${formatCurrency(bet.amount)}`
                              : 'Pending'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No transactions found for the selected criteria
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default Reports;